import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { ReportCadence } from "../entities/project";
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
export const ONE_DAY = 1000 * 60 * 60 * 24;
function compareDateOnly(a, b) {
    const ay = a.getFullYear(), am = a.getMonth(), ad = a.getDate();
    const by = b.getFullYear(), bm = b.getMonth(), bd = b.getDate();
    if (ay < by)
        return -1;
    if (ay > by)
        return 1;
    if (am < bm)
        return -1;
    if (am > bm)
        return 1;
    if (ad < bd)
        return -1;
    if (ad > bd)
        return 1;
    return 0;
}
/**
 * Returns true if a is strictly greater than b (date only).
 */
export function isDateOnlyGreater(a, b) {
    return compareDateOnly(a, b) > 0;
}
/**
 * Returns true if a is strictly less than b (date only).
 */
export function isDateOnlyLess(a, b) {
    return compareDateOnly(a, b) < 0;
}
export function isDateOnlyGreaterOrEqual(a, b) {
    return compareDateOnly(a, b) >= 0;
}
export function isDateOnlyLessOrEqual(a, b) {
    return compareDateOnly(a, b) <= 0;
}
export const formatDate = (date) => {
    return date
        ? `${date.toLocaleString("en-US", {
            month: "short",
            year: "numeric"
        })}`
        : "";
};
export const calculateAge = (birthday) => {
    const today = new Date();
    let age = today.getFullYear() - birthday.getFullYear();
    const monthDiff = today.getMonth() - birthday.getMonth();
    const dayDiff = today.getDate() - birthday.getDate();
    // If the current month and day are before the birthday this year, subtract one year from the age
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }
    return age;
};
export const isValidDate = (value) => {
    try {
        return !!value.getTime();
    }
    catch (_) {
        return false;
    }
};
export const dateSuffix = (d) => {
    if (d > 3 && d < 21)
        return "th";
    switch (d % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};
export const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];
export const makeLocalTime = (utcFromServer) => {
    const localTime = new Date(utcFromServer);
    localTime.setMinutes(localTime.getMinutes() - getOffSet());
    return localTime;
};
export const getOffSet = () => {
    return new Date().getTimezoneOffset();
};
export const getDayDiff = (a, b) => {
    const diffTime = Math.abs(b.getTime() - a.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
export const formatReportDate = (reportDate) => {
    return `${monthNames[reportDate.getMonth()]} ${reportDate.getFullYear()}`;
};
export const formatReportDateSubmission = (reportDate) => {
    return dayjs(reportDate).format("DD/MM/YYYY");
};
/**
 * A `YYYY-MM-DD` report id → `MMM 'YY` axis label (e.g. `2026-07-01 → "Jul '26"`).
 * Shared by the dashboard-surface trend slices (Labour Hours + Social Spend).
 */
export const formatReportMonthLabel = (reportId) => {
    const [year, month] = reportId.split("-");
    const abbr = monthNames[Number(month) - 1]?.substring(0, 3) ?? month;
    return `${abbr} '${year.slice(2)}`;
};
export const formatChartDate = (reportDate) => {
    return `${monthNames[reportDate.getMonth()]
        .substring(0, 3)
        .toLocaleUpperCase()} ${reportDate.getFullYear()}`;
};
export const getCurrentAndPrevMonths = (day, month, year) => {
    const endDate = new Date(year, month, day);
    const startDate = new Date(year, month === 0 ? 11 : month - 1, day);
    return { endDate, startDate };
};
export const dateInfoToDate = (dateInfo, reportingDate) => {
    return new Date(dateInfo.year, dateInfo.month, reportingDate);
};
export const daysUntil = (date) => {
    const today = new Date();
    const diff = date.getTime() - today.getTime();
    return Math.ceil(diff / ONE_DAY);
};
export const getNextOccurrenceDate = (dayOfMonth) => {
    // Get today's date
    const today = new Date();
    // Get the current month and year
    const month = today.getMonth();
    const year = today.getFullYear();
    // Get the next occurrence of the dayOfMonth
    const nextDate = new Date(year, month, dayOfMonth);
    if (nextDate < today) {
        // If the next occurrence has already passed this month,
        // add a month to the date to get the next occurrence.
        nextDate.setMonth(month + 1);
    }
    return nextDate;
};
/**
 * Calendar-date helpers for user-picked days (e.g. an environmental entry's
 * date) that are persisted in a zone-less Postgres `timestamp` column.
 *
 * The value means "this calendar day", not "this instant", so it is pinned to
 * UTC midnight end to end. Without that, a day picked in UTC+10 becomes
 * 14:00 the previous day once serialised to UTC and read back with UTC getters
 * — the classic off-by-one date.
 */
const CALENDAR_DAY = "YYYY-MM-DD";
/**
 * Parses a stored calendar date. `dayjs.utc` reads a zone-less string
 * ("2026-04-07 00:00:00") as UTC, where `new Date(...)` would read it as local
 * time and land on the wrong day.
 */
export const parseCalendarDate = (value) => dayjs.utc(value).toDate();
/**
 * Converts a date produced by a local-time picker into the canonical
 * UTC-midnight instant for the day the user actually selected.
 */
export const calendarDayFromLocal = (date) => dayjs.utc(dayjs(date).format(CALENDAR_DAY), CALENDAR_DAY).toDate();
/**
 * Inverse of {@link calendarDayFromLocal} — the stored day as local midnight,
 * so date pickers (which work in local time) show the day that was saved.
 */
export const calendarDayToLocal = (value) => dayjs(dayjs.utc(value).format(CALENDAR_DAY), CALENDAR_DAY).toDate();
/** Displays a stored calendar date as `dd/MM/yyyy`, free of timezone shift. */
export const formatCalendarDate = (value) => {
    const parsed = value ? dayjs.utc(value) : null;
    return parsed?.isValid() ? parsed.format("DD/MM/YYYY") : "";
};
/**
 * Serialises a calendar date for a zone-less `timestamp` column. Postgres
 * drops the trailing `Z`, so the stored wall clock is the intended day at
 * 00:00 and round-trips through {@link parseCalendarDate} unchanged.
 */
export const calendarDateToTimestampString = (value) => dayjs.utc(value).toISOString();
export const getISOString = (date) => {
    const d = new Date(date), year = String(d.getFullYear());
    let month = String(d.getMonth() + 1), day = String(d.getDate()), hours = String(d.getHours()), minutes = String(d.getMinutes()), seconds = String(d.getSeconds());
    month = month.length < 2 ? "0" + month : month;
    day = day.length < 2 ? "0" + day : day;
    hours = hours.length < 2 ? "0" + hours : hours;
    minutes = minutes.length < 2 ? "0" + minutes : minutes;
    seconds = seconds.length < 2 ? "0" + seconds : seconds;
    return ([year, month, day].join("-") +
        "T" +
        [hours, minutes, seconds].join(":") +
        ".000");
};
export const getDaysDiff = (start, end) => {
    const diff = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diff / (1000 * 3600 * 24));
};
// Month in JavaScript is 0-indexed (January is 0, February is 1, etc),
// but by using 0 as the day it will give us the last day of the prior
// month. So passing in 1 as the month number will return the last day
// of January, not February
export const daysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
};
export const getNumberOfDaysUntilNextReport = (project) => {
    if (!project)
        return 0;
    const reportingDate = project.reportingDueDate;
    const today = new Date();
    const thisMonth = today.getDate() <= reportingDate;
    const nextReportDueDate = new Date(today.getFullYear(), today.getMonth() + (thisMonth ? 0 : 1), reportingDate);
    return getDaysDiff(new Date(), nextReportDueDate);
};
export const getCurrentReportingMonthForProjectReportMonths = (projectReportMonths) => {
    const currentDate = new Date();
    // Filter out dates that are before or equal to the current date
    const futureDates = projectReportMonths.filter((date) => date > currentDate);
    // If there are future dates, return the earliest one; otherwise, return the last date in the list
    if (futureDates.length > 0) {
        return futureDates[0];
    }
    else {
        return projectReportMonths[projectReportMonths.length - 1];
    }
};
export const getLastReportingMonthForProjectReportMonths = (reportDate, projectReportMonths) => {
    // Filter out dates that are before or equal to the current date
    const previousDates = projectReportMonths.filter((date) => date < reportDate);
    // If there are future dates, return the earliest one; otherwise, return the last date in the list
    if (previousDates.length == 0) {
        const monthBefore = new Date(reportDate);
        monthBefore.setMonth(monthBefore.getMonth() - 1);
        return monthBefore;
    }
    else {
        return previousDates[previousDates.length - 1];
    }
};
/**
 * Parses a date string into a `Date`.
 *
 * **Passing `undefined` returns today**, not an invalid date. A missing or
 * not-yet-loaded API field therefore becomes a plausible-looking current date
 * rather than failing visibly. Only call this with a value you know is set,
 * and handle the absent case at the call site.
 */
export const stringToDate = (date, format = "YYYY-MM-DD") => {
    if (!date)
        return new Date();
    return dayjs(date, format).toDate();
};
/**
 * Formats a date that is *already* a reporting date as a `report_id`.
 *
 * Applies no month-end normalisation and no due-day rule, so it is the wrong
 * function for an arbitrary calendar date — use
 * {@link getReportIdForCalendarDate} for that.
 */
export const dateToReportId = (date) => {
    return dayjs(date).format("YYYY-MM-DD");
};
/**
 * Maps a calendar date to `report_id`: last day of the reporting month (YYYY-MM-DD).
 * Same rules as Hammertech hour bucketing (`getReportIdForDate`):
 * - On or before the reporting due day (clamped to that calendar month's length) → that month's report.
 * - After the due day → the following month's report.
 *
 * @param input - Calendar date (`Date` or `YYYY-MM-DD` string).
 * @param reportingDueDate - Project's reporting due day of month (1–31).
 */
export const getReportIdForCalendarDate = (input, reportingDueDate) => {
    const parsed = typeof input === "string"
        ? dayjs(input.trim(), "YYYY-MM-DD", true).startOf("day")
        : dayjs(input).startOf("day");
    if (!parsed.isValid()) {
        throw new Error("Invalid date format. Please use 'YYYY-MM-DD'.");
    }
    const year = parsed.year();
    const monthIndex = parsed.month();
    const dayOfMonth = parsed.date();
    const lastDayThisMonth = parsed.daysInMonth();
    const effectiveDue = Math.min(reportingDueDate, lastDayThisMonth);
    let reportYear;
    let reportMonthIndex;
    if (dayOfMonth <= effectiveDue) {
        reportYear = year;
        reportMonthIndex = monthIndex;
    }
    else {
        const next = parsed.clone().add(1, "month");
        reportYear = next.year();
        reportMonthIndex = next.month();
    }
    return dayjs(new Date(reportYear, reportMonthIndex, 1))
        .endOf("month")
        .format("YYYY-MM-DD");
};
export const getPreviousReportingMonthReportId = (reportId) => {
    const date = stringToDate(reportId);
    const previousMonth = dayjs(date).subtract(1, "month").endOf("month");
    return previousMonth.format("YYYY-MM-DD");
};
export const reportIdToReportingDate = (date, dueDate, reportCadence) => {
    const parsedDate = dayjs(date, "YYYY-MM-DD").startOf("day");
    if (!parsedDate.isValid()) {
        throw new Error("Invalid date format. Please use 'YYYY-MM-DD'.");
    }
    let adjustedDate = parsedDate;
    if (dueDate) {
        const lastDayOfMonth = adjustedDate.daysInMonth();
        adjustedDate = adjustedDate.set("date", Math.min(dueDate, lastDayOfMonth));
    }
    if (reportCadence === ReportCadence.FutureMonth) {
        adjustedDate = adjustedDate.add(1, "month");
        const lastDayOfNextMonth = adjustedDate.daysInMonth();
        adjustedDate = adjustedDate.set("date", Math.min(dueDate, lastDayOfNextMonth));
    }
    return adjustedDate.toDate();
};
export const reportIdToFormattedMonthAndYearShort = (reportId) => {
    const dateValue = stringToDate(reportId);
    return dateValue.toLocaleString("en-US", { month: "short", year: "numeric" });
};
export const reportIdToFormattedMonthAndYear = (reportId) => {
    const dateValue = stringToDate(reportId);
    return dateValue.toLocaleString("en-US", { month: "long", year: "numeric" });
};
/**
 * Format a report date range for display (e.g. "November 2025 - February 2026").
 * Uses same format as reportIdToFormattedMonthAndYear for consistency.
 */
export const reportIdsToFormattedDateRange = (firstReportId, lastReportId) => {
    const first = reportIdToFormattedMonthAndYear(firstReportId);
    const last = reportIdToFormattedMonthAndYear(lastReportId);
    return first === last ? first : `${first} - ${last}`;
};
/**
 * The **month name** for a `report_id` ("February"), despite what the name
 * suggests — it does not return a date. Byte-identical to
 * {@link reportIdToMonth}; prefer that one, which is named for what it returns.
 */
export const reportIdToDate = (date) => {
    const dateValue = stringToDate(date);
    return monthNames[dateValue.getMonth()];
};
/** The month name for a `report_id` ("February"). */
export const reportIdToMonth = (date) => {
    const dateValue = stringToDate(date);
    return monthNames[dateValue.getMonth()];
};
export const reportIdToYear = (date) => {
    const dateValue = stringToDate(date);
    return dateValue.getFullYear();
};
/**
 * Formats a date as the **last day of its month** — note the
 * `endOf("month")`, which matches how `report_id` is keyed (see
 * {@link getReportIdForCalendarDate}).
 *
 * The difference from {@link dateToStringWithoutOffset} is day-of-month, not
 * timezone, which is not what the two names suggest. Use that one to format a
 * date exactly as given.
 */
export const dateToString = (date, format = "YYYY-MM-DD") => {
    return dayjs(date).endOf("month").format(format);
};
export const getDefaultProjectEndDate = (baseDate = new Date()) => {
    return dayjs(baseDate).add(5, "year").endOf("month").format("YYYY-MM-DD");
};
/**
 * Formats a date exactly as given, with no month-end snapping. The counterpart
 * to {@link dateToString} — "WithoutOffset" means without that snap, not
 * without a timezone offset.
 */
export const dateToStringWithoutOffset = (date, format = "YYYY-MM-DD") => {
    return dayjs(date).format(format);
};
export const getPageDate = (selectedMonth) => {
    if (selectedMonth) {
        return `${monthNames[selectedMonth?.getMonth() || new Date().getMonth()]} ${selectedMonth?.getFullYear() || new Date().getFullYear()}`;
    }
    return "";
};
export const formatDateToMMMYYYY = (date) => {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric"
    }).format(date);
};
export const getReportMonths = (start, end, endDateMonthOffset = 0) => {
    let startDate = dayjs(start).startOf("day");
    const effectiveEnd = end ?? new Date();
    const endDate = dayjs(effectiveEnd)
        .add(endDateMonthOffset, "month")
        .startOf("day");
    const result = [];
    // If the end date is before the start date, there are no report months yet.
    if (endDate.isBefore(startDate)) {
        return result;
    }
    while (startDate.isBefore(endDate) || startDate.isSame(endDate)) {
        const month = new Date(startDate.year(), startDate.month() + 1, 0);
        result.push(month);
        startDate = startDate.add(1, "month");
    }
    return result;
};
export const excludeMonths = (reportMonth) => {
    const today = dayjs(new Date()).add(1, "month");
    return reportMonth > today.toDate();
};
export const getMonthDiff = (d1, d2) => {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
};
export const getPercentOfDateProgress = (reportId, startDate, endDate) => {
    const toDate = stringToDate(reportId);
    const numerator = toDate.getTime() - startDate.getTime();
    const denominator = endDate.getTime() - startDate.getTime();
    const percentOfTarget = numerator / denominator;
    return Math.max(Math.min(percentOfTarget, 1), 0);
};
export const getPercentSinceProjectStart = (startDate, endDate, reportId) => {
    const todayDate = reportId ? stringToDate(reportId) : new Date();
    if (endDate.getTime() <= todayDate.getTime()) {
        return 100;
    }
    if (startDate.getTime() >= todayDate.getTime()) {
        return 0;
    }
    const daysSinceProjectStart = Math.max(getDaysDiff(startDate, todayDate), 0);
    const totalDays = Math.max(getDaysDiff(startDate, endDate), 1);
    const percentOfTarget = daysSinceProjectStart / totalDays;
    return Math.min(percentOfTarget, 1) * 100;
};
export const getMaximalDate = (start, end) => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const today = dayjs(new Date());
    if (endDate) {
        if (endDate.isBefore(today)) {
            return dateToString(endDate.toDate());
        }
    }
    if (startDate) {
        if (startDate.isAfter(today)) {
            return dateToString(startDate.toDate());
        }
    }
    return dateToString(today.toDate());
};
export const getTimeSinceCreation = (createdDate) => {
    const diffInSeconds = Math.floor((new Date().getTime() - createdDate.getTime()) / 1000);
    const days = Math.floor(diffInSeconds / (3600 * 24));
    const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;
    if (days > 0) {
        return `${days} day${days == 1 ? "" : "s"} ago`;
    }
    else if (hours > 0) {
        return `${hours} hour${hours == 1 ? "" : "s"} ago`;
    }
    else if (minutes > 0) {
        return `${minutes} minute${minutes == 1 ? "" : "s"} ago`;
    }
    else {
        return `${seconds} second${seconds == 1 ? "" : "s"} ago`;
    }
};
const buildWorkforceProfileMonthsBetweenStartAndEndInclusive = (startMonth, endMonthInclusive) => {
    const out = [];
    let cur = dayjs(startMonth).startOf("month");
    const end = dayjs(endMonthInclusive).endOf("month");
    while (cur.isSameOrBefore(end, "month")) {
        const d = cur.toDate();
        out.push({
            key: dayjs(d).format("YYYY-MM"),
            label: dayjs(d).format("MMM YYYY").toUpperCase()
        });
        cur = cur.add(1, "month");
    }
    return out;
};
/**
 * Workforce profiling columns: first day of the current month through the month
 * containing `projectEndDate` (inclusive).
 */
export const buildWorkforceProfileMonthsFromCurrentMonthUntilEnd = (projectEndDateIso) => {
    const start = dayjs().startOf("month");
    if (!projectEndDateIso) {
        return [];
    }
    const end = dayjs(stringToDate(projectEndDateIso)).endOf("month");
    if (end.isBefore(start, "month")) {
        return [];
    }
    return buildWorkforceProfileMonthsBetweenStartAndEndInclusive(start.toDate(), end.toDate());
};
/**
 * Workforce profiling columns spanning the full project lifetime: the month
 * containing `projectStartDate` through the month containing `projectEndDate`
 * (inclusive). This lets users import numbers into any monthly report,
 * including months that have already passed.
 */
export const buildWorkforceProfileMonthsFromStartUntilEnd = (projectStartDateIso, projectEndDateIso) => {
    if (!projectStartDateIso || !projectEndDateIso) {
        return [];
    }
    const start = dayjs(stringToDate(projectStartDateIso)).startOf("month");
    const end = dayjs(stringToDate(projectEndDateIso)).endOf("month");
    if (end.isBefore(start, "month")) {
        return [];
    }
    return buildWorkforceProfileMonthsBetweenStartAndEndInclusive(start.toDate(), end.toDate());
};
/**
 * Index of the month containing "today" within a workforce-profiling month list.
 * Returns 0 when the current month precedes the range (project not yet started)
 * and the last index when it follows the range (project already ended).
 * Used to position the initial horizontal scroll on the current month.
 */
export const workforceProfileCurrentMonthIndex = (months) => {
    if (months.length === 0) {
        return 0;
    }
    const currentKey = dayjs().startOf("month").format("YYYY-MM");
    const idx = months.findIndex((m) => m.key === currentKey);
    if (idx >= 0) {
        return idx;
    }
    return currentKey < months[0].key ? 0 : months.length - 1;
};
